import { useEffect, useState, useCallback, useRef } from "react";
import { slurpApi, slurpWs } from "./slurpApi";

// Match the backend's default min_usd filter on /api/tokens/:mint/trades
// so dust trades that arrive over the WS don't slip into the trades
// panel after the initial fetch already filtered them out. Keeping this
// in sync with the server-side default is what makes the "after a fresh
// trade lands my $0.50 sniper probe shows up" bug go away.
const TRADES_MIN_USD = 1;

/**
 * Detail-page hook — token row + recent trades, live-updated via WS.
 * After a fresh launch the backend receipt POST might land a few seconds
 * after the user navigates to /token/<mint>. We poll on 404 for up to ~30s
 * so the page comes alive as soon as the backend has the row.
 */
export function useTokenDetail(mint, { tradeLimit = 50 } = {}) {
  const [token, setToken] = useState(null);
  const [trades, setTrades] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollTimer = useRef(null);

  const fetchAll = useCallback(async () => {
    if (!mint) return;
    setIsLoading(true);
    setError(null);
    try {
      const [tokenRes, tradesRes] = await Promise.all([
        slurpApi.getToken(mint),
        slurpApi.getTokenTrades(mint, { limit: tradeLimit }),
      ]);
      setToken(tokenRes.token ?? null);
      setTrades(tradesRes.trades ?? []);
      setError(null);
      return { found: !!tokenRes.token };
    } catch (err) {
      setError(err);
      // 404s are expected for ~5-30s after a fresh launch while the backend
      // catches up. Don't treat them as fatal.
      return { found: false, err };
    } finally {
      setIsLoading(false);
    }
  }, [mint, tradeLimit]);

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    const tick = async () => {
      if (cancelled) return;
      const result = await fetchAll();
      if (cancelled) return;
      // Keep polling if the token isn't there yet and we're within the
      // first ~30s window.
      if (!result?.found && attempt < 15) {
        attempt += 1;
        pollTimer.current = setTimeout(tick, 2000);
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [fetchAll]);

  useEffect(() => {
    if (!slurpWs || !mint) return undefined;
    // Reconnect state lives INSIDE the effect so it resets cleanly when
    // `mint` changes. A bare useRef would carry the "we've disconnected"
    // flag across token navigations and trigger a surprise refetch on
    // the new token's first connect.
    let hadDisconnected = false;
    let lastReconnectFetchAt = 0;

    return slurpWs.subscribe((msg) => {
      if (msg?.type === "trade:new" && msg.trade?.mint === mint) {
        // Apply the same $1 floor the backend's GET applies. Dust
        // sniper-bot probes (sub-cent trades) would otherwise show up
        // in the trades panel only when delivered live, not on reload.
        const sol = Number(msg.trade.sol_raw) / 1e9;
        const usd = sol * Number(msg.trade.sol_usd);
        if (Number.isFinite(usd) && usd >= TRADES_MIN_USD) {
          setTrades((prev) => [msg.trade, ...prev].slice(0, tradeLimit));
        }
        // The trade payload carries the token's updated stats.
        setToken((prev) =>
          prev
            ? {
                ...prev,
                volume_usd_24h: msg.token.volume_usd_24h,
                last_price_usd: msg.token.last_price_usd,
                market_cap_usd: msg.token.market_cap_usd,
                price_change_24h: msg.token.price_change_24h,
              }
            : prev
        );
      } else if (msg?.type === "token:update" && msg.token?.mint === mint) {
        setToken((prev) => (prev ? { ...prev, ...msg.token } : msg.token));
      } else if (msg?.type === "__status") {
        // Close the live-push gap across an API restart: when the WS
        // comes back up after having been down, re-pull the token +
        // recent trades once so the UI catches anything the push layer
        // missed during the outage. Initial connect (no prior
        // disconnect) is skipped — the page's own fetchAll already ran.
        // Throttled to 5s so a flapping socket can't thrash the API.
        if (msg.status === "disconnected") {
          hadDisconnected = true;
        } else if (msg.status === "connected" && hadDisconnected) {
          hadDisconnected = false;
          const now = Date.now();
          if (now - lastReconnectFetchAt > 5_000) {
            lastReconnectFetchAt = now;
            void fetchAll();
          }
        }
      }
    });
  }, [mint, tradeLimit, fetchAll]);

  return { token, trades, isLoading, error, refresh: fetchAll };
}
